import {
  resolvePositionAssignments,
  type ActivatedUserPayload,
} from 'components/IncidentConfig/CreateIncident/createIncidentModel';
import { DEFAULT_ICS_FORM_FAMILY } from 'components/IAP/formFamily';
import {
  defaultDraftSeats,
  rosterNodesForCreate,
} from 'components/IncidentConfig/CreateIncident/draftRosterModel';
import type { Incident } from 'components/IAP/hooks/incidentHooks';
import { PLANNING_PHASES } from 'components/IAP/planningView/USCGPage';
import axios from 'axios';
import config from 'constants/apiConfig';
import {
  focInitialResponseDemoNeedsOaSeed,
  seedFocInitialResponseDemoObjectivesActions,
} from '../focInitialResponseTour/focInitialResponseDemo';

/** Stable Hub name for FOC 10.x Planning P demos (escalated on ensure). */
export const FOC_DEMO_PLANNING_P_NAME = 'FOC Demo — Planning P';

export type FocPlanningPIncident = {
  id: string;
  name?: string | null;
  archived?: number | boolean | null;
  escalated_at?: string | null;
};

const isLive = (incident: FocPlanningPIncident) => !incident.archived;

export function findFocPlanningPDemo(
  incidents: readonly FocPlanningPIncident[] = []
): FocPlanningPIncident | null {
  const demo = incidents.find(
    (incident) =>
      isLive(incident) &&
      String(incident.name ?? '').trim() === FOC_DEMO_PLANNING_P_NAME
  );
  return demo ?? null;
}

export function preferredFocPlanningPId(
  incidents: readonly FocPlanningPIncident[] = []
): string | null {
  const demo = findFocPlanningPDemo(incidents);
  if (demo) return String(demo.id);
  return null;
}

export type EnsureFocPlanningPDemoDeps = {
  incidents: readonly FocPlanningPIncident[];
  groupGuid: string;
  user: {
    user_guid: string;
    name?: string | null;
    email_address?: string | null;
  };
  createIncident: (payload: {
    name: string;
    group_guid: string;
    areas_of_responsibility: string[];
    incident_types: string[];
    ics_form_family: string;
    activated_users: ActivatedUserPayload[];
    roster_nodes: ReturnType<typeof rosterNodesForCreate>;
  }) => Promise<Pick<Incident, 'id' | 'name'> & { escalated_at?: string | null }>;
};

async function seedOaIfNeeded(deps: {
  incidentId: string;
  groupGuid: string;
  userGuid: string;
}): Promise<void> {
  try {
    const needsSeed = await focInitialResponseDemoNeedsOaSeed(deps.incidentId);
    if (!needsSeed) return;
    await seedFocInitialResponseDemoObjectivesActions(deps);
  } catch {
    // Tour still opens; OA steps fall back to empty-state UI.
  }
}

async function escalateIfNeeded(incidentId: string): Promise<boolean> {
  try {
    await axios.post(config.apiGateway.escalateIncident, {
      incident_id: incidentId,
    });
    return true;
  } catch {
    return false;
  }
}

async function seedPlanningCalendarEvents(incidentId: string): Promise<void> {
  for (let i = 0; i < PLANNING_PHASES.length; i += 1) {
    const phase = PLANNING_PHASES[i];
    try {
      await axios.post(
        `${config.apiGateway.ics_incident_calendar_events}/${incidentId}`,
        {
          external_attendees: '',
          location: 'TBD',
          start_date_time: new Date(Date.now() + i * 60 * 60 * 1000),
          end_date_time: new Date(Date.now() + (i + 2) * 60 * 60 * 1000),
          purpose: '',
          title: phase.title,
          color: phase.code,
          internal_attendees: [],
          is_online_meeting: false,
          teams_join_url: null,
        }
      );
    } catch {
      // Meeting Details steps fall back if calendar seed fails.
    }
  }
}

export type EnsureFocPlanningPDemoResult = {
  incidentId: string;
};

/**
 * Returns the Planning P demo id, creating a Training workspace (IC-seated),
 * seeding OA, escalating, and seeding IMH calendar meetings when needed.
 */
export async function ensureFocPlanningPDemoWorkspace(
  deps: EnsureFocPlanningPDemoDeps
): Promise<EnsureFocPlanningPDemoResult | null> {
  const existing = findFocPlanningPDemo(deps.incidents);
  if (existing) {
    const incidentId = String(existing.id);
    if (deps.groupGuid && deps.user.user_guid) {
      await seedOaIfNeeded({
        incidentId,
        groupGuid: deps.groupGuid,
        userGuid: deps.user.user_guid,
      });
    }
    if (!existing.escalated_at) {
      const ok = await escalateIfNeeded(incidentId);
      if (ok) {
        await seedPlanningCalendarEvents(incidentId);
      }
    }
    return { incidentId };
  }

  if (!deps.groupGuid || !deps.user.user_guid) return null;

  const seats = defaultDraftSeats();
  const activatedUser: ActivatedUserPayload = {
    user_guid: deps.user.user_guid,
    role: 'Incident Commander',
    email_address: deps.user.email_address || '',
    name: deps.user.name || deps.user.email_address || 'FOC Demo User',
  };

  const created = await deps.createIncident({
    name: FOC_DEMO_PLANNING_P_NAME,
    group_guid: deps.groupGuid,
    areas_of_responsibility: [],
    incident_types: ['Training'],
    ics_form_family: DEFAULT_ICS_FORM_FAMILY,
    activated_users: [activatedUser],
    roster_nodes: rosterNodesForCreate(seats),
  });

  const incidentId = created?.id ? String(created.id) : null;
  if (!incidentId) return null;

  try {
    const { addUsersToIncidentRoster, getPositionsForIncident } = await import(
      'hooks/incident/useRoster'
    );
    const positions = await getPositionsForIncident(incidentId);
    const positionAssignments = resolvePositionAssignments(
      [
        {
          user_guid: deps.user.user_guid,
          name: activatedUser.name,
          email_address: activatedUser.email_address,
          position: 'Incident Commander',
        },
      ],
      positions
    );
    if (positionAssignments.length > 0) {
      await addUsersToIncidentRoster({
        incidentId,
        groupGuid: deps.groupGuid,
        positionAssignments,
      });
    }
  } catch {
    // Creator remains an activated member.
  }

  await seedOaIfNeeded({
    incidentId,
    groupGuid: deps.groupGuid,
    userGuid: deps.user.user_guid,
  });

  const escalated = await escalateIfNeeded(incidentId);
  if (escalated) {
    await seedPlanningCalendarEvents(incidentId);
  }

  return { incidentId };
}

/**
 * Planning P dashboard href. `phase` is 1-indexed (matches dashboard query).
 */
export function focPlanningPDemoHref(
  incidentId: string,
  tourParam: string,
  phase = 1
): string {
  const params = new URLSearchParams({
    mode: 'future',
    formsView: 'working',
    phase: String(phase),
    [tourParam]: '1',
  });
  return `/iap/${incidentId}/dashboard?${params.toString()}`;
}

export function resolveFocPlanningPHref(
  incidents: readonly FocPlanningPIncident[],
  tourParam: string,
  phase = 1
): string | null {
  const id = preferredFocPlanningPId(incidents);
  if (!id) return null;
  return focPlanningPDemoHref(id, tourParam, phase);
}
